<template>
  <div>
    <ValidationObserver v-slot="{ invalid }">
      <div class="row">
        <div class="col-12 col-xl-3">
          <document-settings :title="$t('message.creditNoteDetails')" form>
            <div slot="body">
              <div>
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <fg-input
                    :label="$t('message.creditNoteReason')"
                    :error="errors[0]"
                  >
                    <b-form-select
                      v-model="creditNote.reason"
                      :options="creditNoteReasonOptions"
                    >
                    </b-form-select>
                  </fg-input>
                </ValidationProvider>
                <div v-if="onlineRefundPossible">
                  <el-tooltip
                    class="item"
                    :content="$t('message.issueOnlineRefundDescription')"
                    placement="right"
                  >
                    <n-checkbox class="mt-3" v-model="creditNote.onlineRefund">
                      {{ $t("message.issueOnlineRefund") }}
                    </n-checkbox>
                  </el-tooltip>
                </div>
              </div>
              <div class="d-none d-xl-block">
                <hr />
                <n-button
                  type="primary"
                  round
                  native-type="submit"
                  @click.native.prevent="submit"
                  :disabled="invalid || !!loader"
                  >{{ $t("message.submit") }}</n-button
                >
                <n-button
                  type="secondary"
                  round
                  simple
                  @click.native.prevent="goBack('/credit-notes')"
                  >{{ $t("message.cancel") }}</n-button
                >
              </div>
            </div>
          </document-settings>
        </div>
        <div class="col-12 col-xl-9">
          <credit-note-form
            :credit-note="creditNote"
            preview
          ></credit-note-form>
        </div>
      </div>
      <!--  ADDITIONAL SAVE BUTTON FOR MOBILE -->
      <div class="row">
        <div class="col d-block d-xl-none">
          <card class="d-flex align-items-center">
            <n-button
              type="primary"
              round
              native-type="submit"
              @click.native.prevent="submit"
              :disabled="invalid || !!loader"
              >{{ $t("message.submit") }}</n-button
            >
            <n-button
              round
              simple
              @click.native.prevent="goBack('/credit-notes')"
              >{{ $t("message.cancel") }}</n-button
            >
          </card>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { dateToHuman } from "@/helpers/helpers.js";
import { DEFAULT_CREDIT_NOTE } from "@/store/accounting/creditNotes";
import CreditNoteForm from "./CreditNoteForm";
import { convertInvoiceToCreditNote } from "@/helpers/documents";
import { goBack } from "@/mixins";
import { Checkbox, DocumentSettings } from "@/components";

export default {
  mixins: [goBack],
  components: {
    CreditNoteForm,
    [Checkbox.name]: Checkbox,
    DocumentSettings
  },
  data() {
    return {
      loader: null,
      creditNoteReasonOptions: [
        {
          value: "REFUND",
          text: "Refund"
        },
        {
          value: "OTHER",
          text: "Other"
        }
      ],
      creditNote: DEFAULT_CREDIT_NOTE
    };
  },
  methods: {
    ...mapActions([
      "fetchUserSettings",
      "fetchInvoice",
      "addCreditNote",
      "startPollingCreditNoteNumberHistory",
      "stopPollingCreditNoteNumberHistory",
      "triggerStripeRefund"
    ]),
    onCreditNoteReasonChange(reason) {
      if (reason !== "REFUND") this.creditNote.onlineRefund = false;
    },
    async submit() {
      this.loader = this.$loading.show();

      try {
        const response = await this.addCreditNote(this.creditNote);
        // this.$router.push(`/credit-notes/view/${response.data.id}`);

        if (this.creditNote.onlineRefund) {
          await this.triggerStripeRefund({
            creditNoteId: response.data.id,
            invoiceId: this.invoice._id || this.invoice.id
          });
        }

        this.$router.push("/credit-notes");
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.submitCreditNoteSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text:
            this.$t("message.submitCreditNoteFailure") + ` ${error.message}.`
        });
      } finally {
        this.loader.hide();
      }
    }
  },
  computed: {
    ...mapGetters(["userSettings", "invoice", "nextCreditNoteNumber"]),
    onlineRefundPossible() {
      return (
        this.creditNote.reason === "REFUND" &&
        this.invoice &&
        this.invoice.stripeChargeId &&
        !this.invoice.stripeRefundProcessed &&
        this.userSettings &&
        this.userSettings.stripeUserId
      );
    }
  },
  filters: {
    dateToHuman
  },
  async created() {
    this.fetchUserSettings();
    this.startPollingCreditNoteNumberHistory();

    const { invoiceId } = this.$route.params;

    if (!invoiceId) this.$router.push("/credit-notes");

    const cachedCreditNote = this.$localStorage.get("creditNote");

    if (cachedCreditNote && cachedCreditNote.invoiceId === invoiceId) {
      this.creditNote = {
        ...this.creditNote,
        ...cachedCreditNote
      };
    }

    try {
      await this.fetchInvoice(invoiceId);

      if (this.creditNote.invoiceId !== invoiceId) {
        this.creditNote = convertInvoiceToCreditNote(this.invoice);
      }
    } catch (error) {
      this.$router.push("/invoices");
      this.$notify({
        type: "danger",
        title: this.$t("message.error"),
        text: this.$t("message.generateCreditNoteFailure")
      });
    }
  },
  destroyed() {
    this.stopPollingCreditNoteNumberHistory();
    this.$localStorage.remove("creditNote");
  },
  watch: {
    nextCreditNoteNumber: {
      immediate: true,
      handler(value) {
        this.creditNote.number = value;
      }
    },
    userSettings: {
      immediate: true,
      handler(value) {
        this.creditNote.from = { ...value.company };
      }
    },
    creditNote: {
      deep: true,
      handler(creditNote) {
        this.$localStorage.set("creditNote", creditNote);
      }
    }
  }
};
</script>
<style lang="scss" scoped></style>
